// ============================================
// 🪜 온보딩 페이지 - HTML 스타일 버전
// ============================================
import React, { useState, useEffect } from 'react';
import { analytics, logEvent } from '../firebase';

function Onboarding({ onComplete }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedGoal, setSelectedGoal] = useState('');
  const [selectedSteps, setSelectedSteps] = useState('');
  const [customSteps, setCustomSteps] = useState('');

  // 온보딩 시작 추적
  useEffect(() => {
    logEvent(analytics, 'onboarding_started');
  }, []);

  // 페이지 전환 추적
  useEffect(() => {
    logEvent(analytics, 'onboarding_page_view', {
      page: currentPage,
      page_name: currentPage === 1 ? 'intro' : currentPage === 2 ? 'frequency' : 'avoidance_reason'
    });
  }, [currentPage]);

  // 계단 이용 빈도 옵션
  const goalOptions = [
    { id: 'morning', label: '출근 시만', description: '아침 운동으로 하루를 시작! ( •̀ᴗ•́ )و' },
    { id: 'always', label: '계단이 보일 때 모두', description: '최고의 효율! 꾸준함이 승리 ᕦ(ò_óˇ)ᕤ' },
    { id: 'evening', label: '퇴근 시에만', description: '하루의 피로를 계단으로 풀어요 (•̀ω•́)✧' },
    { id: 'sometimes', label: '가끔 땡길 때', description: '천천히 시작해봐요 ( ◠‿◠ )' },
  ];

  // 계단 외면 이유 옵션
  const avoidanceReasons = [
    { id: 'lazy', label: '귀찮아서', description: '조금씩 시작하면 습관이 돼요' },
    { id: 'tired', label: '힘들어서', description: '천천히 올라도 괜찮아요' },
    { id: 'no_time', label: '시간이 없어서', description: '하루 1분이면 충분해요' },
    { id: 'elevator', label: '엘리베이터가 편해서', description: '편함을 이기는 건강!' },
    { id: 'no_habit', label: '습관이 안 돼서', description: '오늘부터 함께 시작해봐요' },
  ];

  const handleNext = () => {
    if (currentPage === 1) {
      logEvent(analytics, 'onboarding_page_1_complete');
      setCurrentPage(2);
    } else if (currentPage === 2) {
      if (!selectedGoal) {
        alert('목표를 선택해주세요!');
        return;
      }
      logEvent(analytics, 'onboarding_goal_selected', {
        goal: selectedGoal
      });
      setCurrentPage(3);
    } else if (currentPage === 3) {
      if (!selectedSteps) {
        alert('외면하는 이유를 선택해주세요!');
        return;
      }
      logEvent(analytics, 'onboarding_complete', {
        goal: selectedGoal,
        avoidance_reason: selectedSteps
      });
      // 로컬 스토리지에 저장
      localStorage.setItem('onboardingComplete', 'true');
      localStorage.setItem('userGoal', selectedGoal);
      localStorage.setItem('avoidanceReason', selectedSteps);
      onComplete();
    }
  };

  const handleBack = () => {
    if (currentPage > 1) {
      logEvent(analytics, 'onboarding_back_clicked', {
        from_page: currentPage
      });
      setCurrentPage(currentPage - 1);
    }
  };

  // ============================================
  // 페이지 1: 앱 제작 배경
  // ============================================
  const renderPage1 = () => (
    <div style={styles.pageContainer}>
      <img src="/logo192.png" alt="계단 트래커" style={styles.logo} />

      <div style={styles.divider}></div>

      <div style={styles.storyContent}>
        <p style={styles.storyText}>
          겨울이 되고 운동량이 줄어들어서<br />
          문득 생각했어요.
        </p>
        <p style={styles.storyEmoji}>( •ᴗ• )</p>
        <p style={styles.storyText}>
          "계단만 올라도<br />
          꽤 운동이 되지 않을까?"
        </p>
        
        <p style={styles.storyHighlight}>
          그래서 만들었어요,<br />
          출퇴근 계단 운동 앱!
        </p>
        <p style={styles.storySubtext}>
          당신의 작은 도전을 응원하는 앱
        </p>
      </div>

      <div style={styles.divider}></div>

      <div style={styles.encouragement}>
        함께 시작해볼까요? ᕦ(ò_óˇ)ᕤ
      </div>

      <button style={styles.primaryButton} onClick={() => {
        logEvent(analytics, 'onboarding_start_button_clicked');
        handleNext();
      }}>
        [ 시작하기 ]
      </button>
    </div>
  );

  // ============================================
  // 페이지 2: 목표 선택
  // ============================================
  const renderPage2 = () => (
    <div style={styles.pageContainer}>
      <div style={styles.pageNumber}>2 / 3</div>

      <div style={styles.questionTitle}>
        언제 계단을 이용하시겠어요?
      </div>

      <div style={styles.optionsContainer}>
        {goalOptions.map((option) => (
          <button
            key={option.id}
            style={{
              ...styles.optionButton,
              ...(selectedGoal === option.id ? styles.optionButtonSelected : {}),
            }}
            onClick={() => {
              logEvent(analytics, 'onboarding_frequency_selected', {
                frequency: option.id,
                frequency_label: option.label
              });
              setSelectedGoal(option.id);
            }}
          >
            <div style={styles.optionContent}>
              <div style={styles.optionLabel}>{option.label}</div>
              <div style={styles.optionDescription}>{option.description}</div>
            </div>
          </button>
        ))}
      </div>

      <div style={styles.buttonGroup}>
        <button style={styles.secondaryButton} onClick={handleBack}>
          [ 이전 ]
        </button>
        <button style={styles.primaryButton} onClick={handleNext}>
          [ 다음 ]
        </button>
      </div>
    </div>
  );

  // ============================================
  // 페이지 3: 계단 외면 이유
  // ============================================
  const renderPage3 = () => (
    <div style={styles.pageContainer}>
      <div style={styles.pageNumber}>3 / 3</div>

      <div style={styles.questionTitle}>
        계단을 평소에 외면하는 이유는?
      </div>

      <div style={styles.subtitle2}>
        솔직하게 선택해주세요, 함께 극복해봐요!
      </div>

      <div style={styles.optionsContainer}>
        {avoidanceReasons.map((option) => (
          <button
            key={option.id}
            style={{
              ...styles.optionButton,
              ...(selectedSteps === option.id ? styles.optionButtonSelected : {}),
            }}
            onClick={() => {
              logEvent(analytics, 'onboarding_avoidance_reason_selected', {
                reason: option.id,
                reason_label: option.label
              });
              setSelectedSteps(option.id);
            }}
          >
            <div style={styles.optionContent}>
              <div style={styles.optionLabel}>{option.label}</div>
              <div style={styles.optionDescription}>{option.description}</div>
            </div>
          </button>
        ))}
      </div>

      <div style={styles.buttonGroup}>
        <button style={styles.secondaryButton} onClick={handleBack}>
          [ 이전 ]
        </button>
        <button style={styles.primaryButton} onClick={handleNext}>
          [ 완료 ]
        </button>
      </div>
    </div>
  );

  // ============================================
  // 렌더링
  // ============================================
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {currentPage === 1 && renderPage1()}
        {currentPage === 2 && renderPage2()}
        {currentPage === 3 && renderPage3()}

        {/* 진행 표시 */}
        <div style={styles.progressContainer}>
          <div
            style={{
              ...styles.progressBar,
              width: `${(currentPage / 3) * 100}%`,
            }}
          ></div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// 🎨 스타일
// ============================================
const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    padding: '16px',
  },
  card: {
    backgroundColor: '#fff',
    padding: '32px',
    border: '2px solid #000',
    width: '100%',
    maxWidth: '500px',
    fontSize: '14px',
    position: 'relative',
  },

  // 페이지 공통
  pageContainer: {
    minHeight: '400px',
    display: 'flex',
    flexDirection: 'column',
  },
  pageNumber: {
    fontSize: '12px',
    color: '#666',
    textAlign: 'right',
    marginBottom: '16px',
  },

  // 페이지 1: 로고
  logo: {
    width: '120px',
    height: '120px',
    margin: '0 auto 20px',
    display: 'block',
  },
  subtitle: {
    fontSize: '14px',
    color: '#555',
    textAlign: 'center',
    marginBottom: '20px',
  },
  subtitle2: {
    fontSize: '12px',
    color: '#666',
    textAlign: 'center',
    marginBottom: '16px',
  },
  description: {
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#333',
    textAlign: 'center',
  },

  // 스토리 스타일
  storyContent: {
    textAlign: 'center',
    lineHeight: '1.8',
  },
  storyText: {
    fontSize: '14px',
    color: '#333',
    margin: '8px 0',
  },
  storyEmoji: {
    fontSize: '18px',
    margin: '12px 0',
  },
  storyHighlight: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#000',
    margin: '8px 0',
  },
  storySubtext: {
    fontSize: '13px',
    color: '#666',
    margin: '4px 0',
  },

  encouragement: {
    fontSize: '13px',
    color: '#555',
    textAlign: 'center',
    fontStyle: 'italic',
    marginBottom: '20px',
  },

  // 페이지 2, 3: 질문
  questionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: '24px',
  },

  // 옵션 버튼
  optionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '24px',
    flex: 1,
  },
  optionButton: {
    width: '100%',
    padding: '16px',
    fontSize: '15px',
    border: '1px solid #000',
    backgroundColor: '#fff',
    color: '#000',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '6px',
    textAlign: 'left',
  },
  optionButtonSelected: {
    backgroundColor: '#000',
    color: '#fff',
  },
  optionContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    width: '100%',
  },
  optionLabel: {
    fontSize: '15px',
    fontWeight: '600',
  },
  optionDescription: {
    fontSize: '12px',
    opacity: 0.8,
    lineHeight: '1.4',
  },

  // 계단 수 옵션
  stepsOptionButton: {
    width: '100%',
    padding: '14px',
    fontSize: '15px',
    border: '1px solid #000',
    backgroundColor: '#fff',
    color: '#000',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  stepsValue: {
    fontSize: '16px',
    fontWeight: '600',
  },
  caloriesValue: {
    fontSize: '13px',
    color: '#666',
  },

  // 직접 입력
  customInputContainer: {
    border: '1px solid #000',
    padding: '12px',
    backgroundColor: '#fff',
  },
  customInputSelected: {
    backgroundColor: '#f0f0f0',
  },
  customLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    cursor: 'pointer',
  },
  radio: {
    cursor: 'pointer',
  },
  customInputWrapper: {
    marginTop: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  customInput: {
    flex: 1,
    padding: '8px',
    fontSize: '14px',
    border: '1px solid #000',
  },
  customUnit: {
    fontSize: '14px',
  },
  customCalories: {
    fontSize: '12px',
    color: '#666',
  },

  // 버튼
  primaryButton: {
    width: '100%',
    padding: '12px',
    fontSize: '15px',
    fontWeight: '600',
    border: '1px solid #000',
    backgroundColor: '#808080',
    color: '#fff',
    cursor: 'pointer',
  },
  secondaryButton: {
    width: '100%',
    padding: '12px',
    fontSize: '15px',
    fontWeight: '500',
    border: '1px solid #000',
    backgroundColor: '#fff',
    color: '#000',
    cursor: 'pointer',
  },
  buttonGroup: {
    display: 'flex',
    gap: '12px',
  },

  // 진행 표시
  progressContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '4px',
    backgroundColor: '#ddd',
  },
  progressBar: {
    height: '100%',
    backgroundColor: '#000',
    transition: 'width 0.3s ease',
  },

  divider: {
    height: '1px',
    backgroundColor: '#ddd',
    margin: '16px 0',
  },
};

export default Onboarding;
